import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCamera } from 'react-icons/fi';
import promiseRequest from '@/utils/promiseToast';
import profAvatar from '@/assets/images/prof/prof-avatar-3d.png';
import { Modal as SimpleModal } from '@/components/custom/modal/modal';
import { Loader } from '@/components/custom/loader';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { uploadFileFromPresignedUrl } from '@/utils/aws-sdk';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import { useSession } from '@/hooks/useSession';
import usePlayfulNav from '@/hooks/usePlayfulNav';
import { DashboardContent } from '@/layouts/dashboard';
import { Header } from '@/components/custom/header/header';
import { Button } from '@/components/ui/button';
import { InputFile } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { TiaBanner } from '@/components/custom/tia-banner';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import arrowRight from '@/assets/images/icons/arrow-right.png';
import { PATHS } from '@/routes/paths';
import { cn } from '@/lib/utils';

// Espelha EssaySendPlayful (aluno), trocando só o necessário pro fluxo do
// professor: imagem do banner (personagem do professor em vez da T.I.A.),
// textos em 3ª pessoa (o professor envia EM NOME do aluno) e as diferenças de
// comportamento que existem entre o aluno enviando a própria redação e o
// professor enviando pelo aluno (ver essay-send-content.jsx, versão Default):
// atribuição do envio ao aluno (user_id), busca com show_for_student_id,
// detecção de "outro professor já enviou" via ActionCable, e navegação de
// volta pro acompanhamento (não pro "sobre", que é tela de aluno).
const MAX_DIM = 10000;

function processImage(file) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => {
          if (!blob) return resolve(file);
          resolve(new File([blob], file.name, { type: file.type }));
        }, file.type);
      } else {
        URL.revokeObjectURL(img.src);
        resolve(file);
      }
    };
    img.src = URL.createObjectURL(file);
  });
}

export function EssaySendPlayfulTeacher({ essayId, studentId, uri }) {
  const navigate = useNavigate();
  const { session } = useSession();
  const playfulNav = usePlayfulNav();
  const [imageLabel, setImageLabel] = useState();
  const [openLoaderModal, setOpenLoaderModal] = useState(false);
  const [openImgModal, setOpenImgModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [webhookNotification, setWebhookNotification] = useState(null);
  const [sendingByTeacherId, setSendingByTeacherId] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const { promiseToast } = promiseRequest();
  const client = useClient();
  const [essayText, setEssayText] = useState('');
  const [essayStudentId, setEssayStudentId] = useState('');
  const [essayTitle, setEssayTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);

  const getEssayStatus = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`/essays/${essayId}?show_for_student_id=${studentId}`);
    if (error) {
      toast.error('Erro ao buscar status da redação');
    } else {
      setEssayTitle(data?.title ? data?.title : data?.topic);
      const status = data['essay-student'].status;
      const studentEssayId = data['essay-student'].id;
      setEssayStudentId(data['essay-student']?.id);

      if (status === 'ocr_review') {
        const { data, error } = await client.fetch(`/essays/${essayId}/essay_students/${studentEssayId}`);
        if (error) {
          toast.error('Erro ao buscar status da redação');
        } else {
          setEssayText(data?.body);
          setImageLabel(data?.['essay_image']?.url);
        }
      }
    }
    setLoading(false);
  };

  const transcribeEssay = async e => {
    setOpenLoaderModal(true);
    let file = e.target.files[0];
    if (!file) return;
    try {
      file = await processImage(file);
    } catch (err) {
      console.warn('resize falhou, usando original', err);
    }
    setSendingByTeacherId(session?.user?.id);

    const formData = { filename: file.name, content_type: file.type, user_id: studentId };

    try {
      const postRes = await client.mutate(`/essays/${essayId}/essay_students`, formData);
      const uploadUrl = decodeURI(postRes.data['upload-url']);
      const newEssayStudentId = postRes.data.id;

      const uploadedFileUrl = await uploadFileFromPresignedUrl(uploadUrl, file);
      const fileName = uploadedFileUrl.split('/').pop();

      const patchRes = await client.mutate(`/essays/${essayId}/essay_students/${newEssayStudentId}`, { essay_image_name: fileName }, { method: 'PATCH' });

      if (patchRes.error) {
        toast.error(patchRes.error.message);
        return;
      }

      const patchData = patchRes.data;

      if (!patchData.body || patchData.body.length < 100) {
        setOpenErrorModal(true);
        setEssayText(null);
      } else {
        setEssayText(patchData.body);
        setEssayStudentId(patchData.id);
        setImageLabel(patchData.essay_image.url);
        setOpenSuccessModal(true);
      }
    } catch (err) {
      toast.error('Houve um problema ao enviar a imagem da redação.');
      console.error(err);
    } finally {
      setOpenLoaderModal(false);
    }
  };

  const sendEssay = async () => {
    promiseToast({
      url: `/essays/${essayId}/essay_students/${essayStudentId}/correction`,
      request: { essay_student_content_attributes: { body: essayText } },
      successText: 'Redação enviada com sucesso',
      errorText: 'Erro ao enviar redação',
      onSuccess: () => navigate(PATHS.ESSAY.MONITORING(essayId))
    });
  };

  useEffect(() => {
    getEssayStatus();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = essayText;
    }
  }, [essayText]);

  const handleReceivedNotification = data => {
    setWebhookNotification(data);
  };

  useEffect(() => {
    if (!webhookNotification) return;

    if (webhookNotification?.id && sendingByTeacherId !== session?.user?.id && parseInt(studentId) === webhookNotification?.user_id && webhookNotification?.status === 'sending_by_teacher') {
      toast.error('Outro professor já enviou essa redação.');
      navigate(PATHS.ESSAY.MONITORING(essayId));
    }
    // eslint-disable-next-line
  }, [webhookNotification]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const breadcrumbs = {
    title: 'Redação',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: PATHS.ESSAY.MONITORING(essayId),
        name: essayTitle,
        isActive: false
      },
      {
        route: uri,
        name: 'Enviando a redação',
        isActive: true
      }
    ]
  };

  return (
    <>
      {essayId && studentId && (
        <ActionCableConsumer
          channel="EssayStudentChannel"
          setObjectChannel={handleReceivedNotification}
          channelKey={'essay_id'}
          channelId={essayId}
        />
      )}

      <Header playfulNav={playfulNav} />

      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <TiaBanner
              className={cn('mb-6', imageLabel ? 'md:mt-12' : 'md:mt-28')}
              image={JOINHA}
              imageHeight={215}
              imageOffset={200}
              imageAlt="Personagem do professor"
              bubbleMaxWidth={800}
              text={
                imageLabel
                  ? 'Yeah! Já transformei a foto da redação do aluno em texto!\nDê uma olhada para ver se ficou tudo direitinho. Se alguma palavra estiver errada, é só clicar no texto e consertar!\n• Ficou perfeito? Clique em "Enviar a redação".\n• Quer tentar de novo? Clique em "Enviar outra foto".'
                  : 'Já tem a foto da redação do aluno? Toque no botão abaixo para enviá-la.'
              }
            />

            {imageLabel && (
              <div className="mb-6 flex justify-end">
                <button
                  type="button"
                  onClick={sendEssay}
                  className="block rounded-full px-5 py-2.5 text-sm font-extrabold text-white transition-transform duration-100 active:translate-y-px"
                  style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
                >
                  Enviar a redação
                </button>
              </div>
            )}

            {imageLabel ? (
              <div className="flex flex-col items-stretch gap-4 lg:flex-row">
                <div className="flex max-h-[700px] flex-1 flex-col overflow-y-auto rounded-2xl border border-neutral-lightest bg-white p-5 shadow-card">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="mb-0 text-base font-extrabold text-neutral-900">Foto da redação do aluno</h3>
                    <InputFile
                      variant="button"
                      buttonVariant="link"
                      buttonClassName="p-0 h-5 text-sm font-semibold"
                      buttonLabel="Enviar outra foto"
                      ref={fileInputRef}
                      type="file"
                      name="image"
                      id="image"
                      accept=".jpg, .jpeg, .png, .heic, .heif"
                      onChange={transcribeEssay}
                    />
                  </div>
                  <img
                    src={imageLabel}
                    alt="imagem da redação"
                    className="w-full cursor-pointer rounded-xl"
                    onClick={() => setOpenImgModal(true)}
                  />
                </div>

                <img
                  src={arrowRight}
                  alt=""
                  className="mx-auto hidden h-10 w-10 shrink-0 self-center lg:block"
                />

                <div className="flex max-h-[700px] flex-1 flex-col rounded-2xl border-2 border-[var(--color-mission)] bg-white p-5 shadow-card">
                  <h3 className="mb-3 shrink-0 text-base font-extrabold text-neutral-900">Revise o texto do aluno</h3>
                  <Textarea
                    ref={textareaRef}
                    className="min-h-0 flex-1 resize-none overflow-y-auto border-none p-0 text-sm text-neutral-dark shadow-none focus-visible:ring-0"
                    onBlur={() => setEssayText(textareaRef.current?.value)}
                  >
                    {essayText}
                  </Textarea>
                </div>
              </div>
            ) : (
              <div className="mx-auto flex h-[500px] min-w-[400px] max-w-[50%] flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-neutral-lighter bg-white px-8 text-center">
                <p className="mb-0 text-sm font-extrabold text-neutral-900">Toque no botão abaixo para enviar a redação do aluno</p>
                <p className="mb-6 text-sm text-neutral-dark">A foto deve estar em jpg, jpeg, png, heic ou heif</p>
                <label className="cursor-pointer">
                  <input
                    className="hidden"
                    type="file"
                    name="image"
                    id="image"
                    accept=".jpg, .jpeg, .png, .heic, .heif"
                    onChange={transcribeEssay}
                  />
                  <span
                    className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-extrabold text-white transition-transform duration-100 active:translate-y-px"
                    style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
                  >
                    <FiCamera />
                    Enviar foto da redação
                  </span>
                </label>
              </div>
            )}
          </>
        )}
      </DashboardContent>

      {/* Modais abaixo permanecem exatamente como no tema padrão/aluno, sem alteração visual */}
      <Dialog
        open={openErrorModal}
        onOpenChange={() => setOpenErrorModal(false)}
      >
        <DialogContent size="sm">
          <div style={{ textAlign: 'center' }}>
            <DialogTitle className="sr-only">Arquivo inválido</DialogTitle>
            <p className="mb-4">A imagem enviada não parecer ser de uma redação. Envie uma imagem válida para seguirmos com a correção</p>
            <label className="form__custom-file-input u-d-inline-block">
              <input
                type="file"
                name="image"
                id="image"
                accept=".jpg, .jpeg, .png, .heic, .heif"
                onChange={transcribeEssay}
              />
              <span className="btn btn--primary btn--wide">Enviar outra foto</span>
            </label>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openSuccessModal}
        onOpenChange={() => setOpenSuccessModal(false)}
      >
        <DialogContent size="md">
          <DialogTitle className="sr-only">Revise</DialogTitle>

          <div
            style={{
              display: isMobile ? 'block' : 'flex',
              textAlign: isMobile ? 'center' : 'left',
              alignItems: 'center',
              gap: '32px',
              maxWidth: '700px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={JOINHA}
                alt="Personagem do professor"
                style={{ height: '250px' }}
              />
            </div>

            <div style={{ flex: '1', padding: '12px' }}>
              <h2 style={{ color: 'var(--color-neutral-900)', fontSize: '24px', fontWeight: 700, marginBottom: '16px' }}>Redação digitalizada com sucesso!</h2>
              <p style={{ fontSize: '16px', marginBottom: '32px' }}>
                Agora revise o resultado da digitalização e clique em <strong>Enviar para revisão</strong>
              </p>
              <Button
                style={{ width: '160px' }}
                onClick={() => setOpenSuccessModal(false)}
                variant="mission"
              >
                Entendi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openImgModal}
        onOpenChange={() => setOpenImgModal(false)}
      >
        <DialogContent size="lg">
          <DialogTitle className="sr-only">Prova digitalizada</DialogTitle>
          <img
            src={imageLabel}
            alt="prova digitalizada"
          />
        </DialogContent>
      </Dialog>

      <SimpleModal
        show={openLoaderModal}
        onClose={() => setOpenLoaderModal(false)}
        hideCloseButton
        closeOnOverlayClick={false}
      >
        <div className="flex items-start gap-3">
          <AvatarWrapper
            size="md"
            src={profAvatar}
            text="Professor"
            className="border border-neutral-lighter"
          />
          <div className="grow">
            <span className="font-bold text-primary">Aguarde enquanto digitalizamos a redação</span>
            <p className="mb-4">Não feche essa tela</p>
            <Loader />
          </div>
        </div>
      </SimpleModal>
    </>
  );
}
